import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';

export default function PublishArticle() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    title: '', summary: '', source_url: '', source: '', media_type: 'image',
    image_url: '', video_url: '', thumbnail_url: '',
    category: '', city: '', is_breaking: false,
  });

  useEffect(() => {
    api.get('/categories').then(res => {
      setCategories(res.data.data);
      if (res.data.data.length > 0) setForm(f => ({ ...f, category: res.data.data[0].slug }));
    }).catch(() => {});
    api.get('/cities').then(res => {
      setCities(res.data.data);
      if (res.data.data.length > 0) setForm(f => ({ ...f, city: res.data.data[0].slug }));
    }).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const wordCount = form.summary.trim().split(/\s+/).filter(Boolean).length;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleUpload = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'video' ? 'video/mp4' : 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = type === 'image' ? '/admin/upload/image'
        : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

      setUploading(true);
      try {
        const res = await api.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = res.data.data.url;
        if (type === 'image') set('image_url', url);
        else if (type === 'thumbnail') set('thumbnail_url', url);
        else set('video_url', url);
        toast.success(`${type} uploaded`);
      } catch (err) {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary) return toast.error('Title and summary required');

    setLoading(true);
    try {
      await api.post('/admin/articles', form);
      toast.success('Article published!');
      navigate('/articles');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Publish Article</h2>
      </div>

      <div className="publish-layout">
        <form className="publish-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title <span className="char-count">{form.title.length}/300</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Article headline..."
              maxLength={300}
              required
            />
          </div>

          <div className="form-group">
            <label>
              Summary
              <span className={`char-count ${wordCount >= 60 && wordCount <= 80 ? 'good' : wordCount > 80 ? 'over' : ''}`}>
                {wordCount} words
              </span>
            </label>
            <textarea
              value={form.summary}
              onChange={(e) => set('summary', e.target.value)}
              placeholder="Write a 60-80 word summary..."
              rows={5}
              required
            />
          </div>

          <div className="form-group">
            <label>Source URL (optional)</label>
            <input
              type="url"
              value={form.source_url}
              onChange={(e) => set('source_url', e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label>Source Name (optional)</label>
            <input
              type="text"
              value={form.source}
              onChange={(e) => set('source', e.target.value)}
              placeholder="e.g. Reuters, BBC News..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Media Type</label>
              <div className="radio-group">
                <label className={`radio-card ${form.media_type === 'image' ? 'selected' : ''}`}>
                  <input type="radio" name="media_type" value="image"
                    checked={form.media_type === 'image'}
                    onChange={() => set('media_type', 'image')} />
                  Image
                </label>
                <label className={`radio-card ${form.media_type === 'video' ? 'selected' : ''}`}>
                  <input type="radio" name="media_type" value="video"
                    checked={form.media_type === 'video'}
                    onChange={() => set('media_type', 'video')} />
                  Video
                </label>
              </div>
            </div>
          </div>

          {form.media_type === 'image' && (
            <div className="form-group">
              <label>Image</label>
              <div className="upload-area" onClick={() => handleUpload('image')}>
                {form.image_url ? (
                  <img src={form.image_url} alt="preview" className="upload-preview" />
                ) : (
                  <p>{uploading ? 'Uploading...' : 'Click to upload image (max 5MB)'}</p>
                )}
              </div>
              <input type="text" value={form.image_url} onChange={(e) => set('image_url', e.target.value)}
                placeholder="Or paste image URL" className="mt-sm" />
            </div>
          )}

          {form.media_type === 'video' && (
            <>
              <div className="form-group">
                <label>Thumbnail</label>
                <div className="upload-area" onClick={() => handleUpload('thumbnail')}>
                  {form.thumbnail_url ? (
                    <img src={form.thumbnail_url} alt="thumbnail" className="upload-preview" />
                  ) : (
                    <p>{uploading ? 'Uploading...' : 'Click to upload thumbnail (max 2MB)'}</p>
                  )}
                </div>
                <input type="text" value={form.thumbnail_url} onChange={(e) => set('thumbnail_url', e.target.value)}
                  placeholder="Or paste thumbnail URL" className="mt-sm" />
              </div>
              <div className="form-group">
                <label>Video</label>
                <div className="upload-area" onClick={() => handleUpload('video')}>
                  <p>{form.video_url ? '✓ Video uploaded' : uploading ? 'Uploading...' : 'Click to upload video (max 200MB)'}</p>
                </div>
                <input type="text" value={form.video_url} onChange={(e) => set('video_url', e.target.value)}
                  placeholder="Or paste video URL" className="mt-sm" />
              </div>
            </>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select value={form.city} onChange={(e) => set('city', e.target.value)}>
                {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_breaking}
                onChange={(e) => set('is_breaking', e.target.checked)} />
              <span className="breaking-tag">Breaking News</span> — sends push notification
            </label>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Publishing...' : 'Publish Article'}
          </button>
        </form>

        {/* Live Preview */}
        <div className="preview-panel">
          <h3>Preview</h3>
          <div className="phone-preview">
            <div className="preview-card">
              {(form.image_url || form.thumbnail_url) && (
                <img src={form.image_url || form.thumbnail_url} alt="" className="preview-img" />
              )}
              <div className="preview-body">
                <span className="preview-category">{form.category.replace('_', ' ')}</span>
                <h4>{form.title || 'Article title...'}</h4>
                <p>{form.summary || 'Article summary will appear here...'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
